// Format market cap values according to specified rules
export const formatMarketCap = (value) => {
  if (!value) return '$0';
  
  const num = Number(value);
  
  // Below 1M: whole number with K (e.g., $25K, $500K, $950K)
  if (num < 1_000_000) {
    return `$${Math.round(num / 1000)}K`;
  }
  
  // Below 100M: one decimal with M (e.g., $5.2M, $25.9M, $95.7M)
  if (num < 100_000_000) {
    return `$${(num / 1_000_000).toFixed(1)}M`;
  }
  
  // Below 1B: whole number with M (e.g., $105M, $900M)
  if (num < 1_000_000_000) {
    return `$${Math.round(num / 1_000_000)}M`;
  }
  
  // Above 1B: one decimal with B (e.g., $1.2B, $15.7B)
  return `$${(num / 1_000_000_000).toFixed(1)}B`;
};

// Format percentage change to whole numbers
export const formatPercentage = (current, previous) => {
  if (!current || !previous) return null;
  const change = ((current - previous) / previous) * 100;
  return Math.round(change);
};

// Format subscriber count with K/M suffixes
export const formatSubscribers = (value) => {
  if (!value) return '0';
  
  const num = Number(value);
  
  // Below 1K: show full number (e.g., 123, 999)
  if (num < 1000) {
    return new Intl.NumberFormat().format(num);
  }
  
  // Below 1M: one decimal with K (e.g., 24.5K)
  if (num < 1_000_000) {
    return `${(num / 1000).toFixed(1)}K`.replace('.0K', 'K');
  }
  
  // Above 1M: one decimal with M (e.g., 1.2M)
  return `${(num / 1_000_000).toFixed(1)}M`.replace('.0M', 'M');
};

// Format time ago
export const formatTimeAgo = (timestamp) => {
  if (!timestamp) return '';
  
  let date;
  
  // Handle millisecond timestamps (like pairCreatedAt from DexScreener)
  if (typeof timestamp === 'number' || /^\d+$/.test(timestamp)) {
    date = new Date(Number(timestamp));
  } else {
    // Handle ISO string timestamps
    date = new Date(timestamp);
  }
  
  if (isNaN(date.getTime())) return '';
  
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  
  if (months > 0) {
    return `${months}mo ago`;
  }
  
  if (weeks > 0) {
    return `${weeks}w ago`;
  }
  
  if (days > 0) {
    return `${days}d ago`;
  }
  
  if (hours > 0) {
    return `${hours}h ago`;
  }
  
  if (minutes > 0) {
    return `${minutes}m ago`;
  }
  
  return 'just now';
};

// Format gain/loss percentage with sign
export const formatGainLoss = (value) => {
  if (!value && value !== 0) return '0%';
  
  const num = Number(value);
  // Always show sign for non-zero values
  return num > 0 ? `+${num}%` : `${num}%`;
};